import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { caminhoDoPdfEmitido } from '../api/cptu';
import { data } from '../formato';
import { Carregando, ErroDaApi, LinkProtegido, Selo, Vazio, type Tom } from '../ui';
import { Seletor, SeletorDeCliente } from '../Filtros';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista,
  usePaginacao, type FiltroAtivo,
} from '../BarraDeLista';
import { Periodo, type ValorDoPeriodo } from '../Periodo';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Os documentos emitidos (onda 272).
 *
 * «Relatórios passa a ter uma lista dos documentos emitidos? Sim» —
 * Humberto, 04/10/2026. A tela de Relatórios montava o caderno de UMA OS;
 * o que já tinha saído — qual laudo, em que revisão, se está no portal —
 * não tinha lugar para ser procurado. Esta é a lista, no padrão Carbon
 * Testesolo, e o caderno continua na outra aba.
 *
 * O número diz o tipo (decisão de 02/10): laudo de ensaio é
 * `<OS>-<método>-<nn>`; relatório técnico é `<OS>-RT<nn>`. A revisão
 * mantém o número (R00, R01…).
 *
 * Onda 315: o relatório da campanha CPTu (`<OS>-CPTU-nn`) entra com o tipo
 * escrito («Relatório de ensaio de campo — CPTu», o mesmo da autenticidade),
 * «Abrir a campanha» (a aba Relatório) e o PDF emitido pelos bytes do acervo
 * (`/cptu/relatorios/:id/pdf`) — a API traz a campanha em `cptu_campanha_id`.
 */

interface Documento {
  id: string;
  numero: string | null;
  revisao: number;
  status: string;
  tipo: 'laudo' | 'rt';
  data_aprovacao: string | null;
  data_emissao: string | null;
  disponibilizado_em: string | null;
  recolhido_em: string | null;
  motivo_recolhimento: string | null;
  tem_pdf: boolean;
  os_id: string;
  os_numero: string;
  cliente: string | null;
  obra_nome: string | null;
  ensaios: number;
  ensaio_id: string | null;
  metodos: string | null;
  /** O relatório da campanha CPTu (onda 315): a campanha que aponta para ele. */
  cptu_campanha_id?: string | null;
}

/** O tipo escrito do documento — o CPTu pelo nome da autenticidade (onda 315). */
const tipoDoDocumento = (r: Documento) => (r.cptu_campanha_id ? 'Relatório de ensaio de campo — CPTu'
  : r.tipo === 'rt' ? 'Relatório técnico' : 'Laudo de ensaio');

const TOM: Record<string, Tom> = {
  aprovado: 'andamento', emitido: 'ok', disponibilizado: 'ok',
  substituido: 'neutro', cancelado: 'neutro',
};
const TEXTO: Record<string, string> = {
  aprovado: 'Aprovado, a emitir', emitido: 'Emitido', disponibilizado: 'No portal',
  substituido: 'Substituído', cancelado: 'Cancelado',
};

const rev = (n: number) => `R${String(n).padStart(2, '0')}`;

export function DocumentosEmitidos() {
  const [linhas, setLinhas] = useState<Documento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [cliente, setCliente] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<ValorDoPeriodo>({ de: '', ate: '' });
  const [recolhidos, setRecolhidos] = useState(false);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      if (cliente) q.set('cliente', cliente);
      if (status) q.set('status', status);
      if (tipo) q.set('tipo', tipo);
      if (periodo.de) q.set('de', periodo.de);
      if (periodo.ate) q.set('ate', periodo.ate);
      if (recolhidos) q.set('todos', 'true');
      setLinhas(await api<Documento[]>(`/relatorios?${q}`));
    } catch (e) { setErro(e); }
  }, [busca, cliente, status, tipo, periodo.de, periodo.ate, recolhidos]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  const pag = usePaginacao(linhas ?? []);
  const ativos: FiltroAtivo[] = [];
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) });
  if (status) ativos.push({ chave: 'st', texto: `Situação: ${TEXTO[status] ?? status}`, tirar: () => setStatus(null) });
  if (tipo) ativos.push({ chave: 'tp', texto: tipo === 'rt' ? 'Relatórios técnicos' : 'Laudos de ensaio', tirar: () => setTipo(null) });
  if (periodo.de || periodo.ate) {
    ativos.push({ chave: 'per', texto: `Emitidos: ${periodo.de ? data(periodo.de) : '…'} – ${periodo.ate ? data(periodo.ate) : '…'}`, tirar: () => setPeriodo({ de: '', ate: '' }) });
  }
  if (recolhidos) ativos.push({ chave: 'rec', texto: 'Com substituídos e cancelados', tirar: () => setRecolhidos(false) });

  return (
    <>
      <BarraDeLista rotulo="Filtros dos documentos emitidos">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Número do documento, OS, cliente ou obra" rotulo="Buscar documento" />
        <Periodo valor={periodo} aoMudar={setPeriodo} rotulo="Emitidos"
          atalhos={['semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
        <Seletor naBarra rotulo="Tipo" valor={tipo} aoMudar={setTipo} todos="Todos os tipos"
          opcoes={[{ valor: 'laudo', texto: 'Laudo de ensaio' }, { valor: 'rt', texto: 'Relatório técnico' }]} />
        <Seletor naBarra rotulo="Situação" valor={status} aoMudar={setStatus} todos="Todas"
          opcoes={['aprovado', 'emitido', 'disponibilizado'].map((s) => ({ valor: s, texto: TEXTO[s] }))} />
        <AlternarDaBarra ligado={recolhidos} aoMudar={setRecolhidos}>
          {recolhidos ? '✓ Com os recolhidos' : 'Com os recolhidos'}
        </AlternarDaBarra>
      </BarraDeLista>

      <FiltrosAtivos itens={ativos}
        aoLimpar={() => {
          setBusca(''); setCliente(null); setStatus(null); setTipo(null);
          setPeriodo({ de: '', ate: '' }); setRecolhidos(false);
        }}
        contagem={linhas ? `${linhas.length} ${linhas.length === 1 ? 'documento' : 'documentos'}` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando texto="Reunindo os documentos" />
        : (
          <div className="cartao">
            {linhas.length === 0 ? (
              <Vazio>
                {ativos.length
                  ? 'Nenhum documento com estes filtros. Use «Limpar filtros» para ver todos.'
                  : 'Nenhum documento aprovado ainda. Um laudo entra aqui quando o ensaio é aprovado e o relatório é emitido.'}
              </Vazio>
            ) : (
              <TabelaComFicha
                linhas={pag.visiveis}
                chave={(r) => r.id}
                nome={(r) => `Documento ${r.numero ?? ''} ${rev(r.revisao)}`}
                rotulo="Documentos emitidos"
                classeDaLinha={(r) => (['substituido', 'cancelado'].includes(r.status) ? 'apagada' : undefined)}
                colunas={COLUNAS}
                ficha={fichaDoDocumento}
              />
            )}
            <RodapeDaLista {...pag} teto={500} ordem="emitido em ↓" />
            <p className="rodape-regra">
              O número do laudo diz a OS e o ensaio (<span className="mono">2606101-ADNS-01</span>);
              o relatório técnico que reúne ensaios é <span className="mono">2606101-RT01</span>.
              Corrigir um documento emitido gera nova revisão com o mesmo número — o anterior
              fica aqui como substituído.
            </p>
          </div>
        )}
    </>
  );
}

const COLUNAS: ColunaComFicha<Documento>[] = [
  {
    chave: 'doc',
    cabeca: <th scope="col">Documento</th>,
    celula: (r) => (
      <th scope="row">
        <strong className="mono">{r.numero ?? '—'}</strong>{' '}
        <span className="sub mono">{rev(r.revisao)}</span>
        <span className="sub">{tipoDoDocumento(r)}{r.metodos ? ` · ${r.metodos}` : ''}</span>
      </th>
    ),
  },
  {
    chave: 'os',
    cabeca: <th scope="col">OS e cliente</th>,
    celula: (r) => (
      <td>
        <Link to={`/os/${r.os_id}`} className="mono">OS {r.os_numero}</Link>
        <span className="sub">{[r.cliente, r.obra_nome].filter(Boolean).join(' · ')}</span>
      </td>
    ),
  },
  {
    chave: 'emissao', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Emitido em</th>,
    celula: (r) => (
      <td className="col-opcional">
        {r.data_emissao ? data(r.data_emissao) : <span className="nada">ainda não</span>}
        {r.data_aprovacao && <span className="sub">aprovado {data(r.data_aprovacao)}</span>}
      </td>
    ),
  },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (r) => (
      <td>
        <Selo tom={TOM[r.status] ?? 'neutro'}>{TEXTO[r.status] ?? r.status}</Selo>
        {r.disponibilizado_em && <span className="sub">no portal desde {data(r.disponibilizado_em)}</span>}
      </td>
    ),
  },
];

function fichaDoDocumento(r: Documento): FichaDaLinha {
  return {
    rotulo: tipoDoDocumento(r),
    titulo: `${r.numero ?? '—'} · ${rev(r.revisao)}`,
    tituloCodigo: true,
    sub: <span>OS {r.os_numero}{r.cliente ? ` · ${r.cliente}` : ''}</span>,
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM[r.status] ?? 'neutro'}>{TEXTO[r.status] ?? r.status}</Selo>
        </div>
        {r.recolhido_em && (
          <div className="tf-aviso atencao">
            Recolhido em {data(r.recolhido_em)}{r.motivo_recolhimento ? ` — «${r.motivo_recolhimento}»` : ''}.
          </div>
        )}
        <CamposDaFicha itens={[
          ['Obra', r.obra_nome],
          r.cptu_campanha_id ? ['Ensaios', 'Campanha CPTu'] : ['Ensaios', r.metodos ? `${r.ensaios} · ${r.metodos}` : String(r.ensaios)],
          ['Aprovado em', r.data_aprovacao ? data(r.data_aprovacao) : null],
          ['Emitido em', r.data_emissao ? data(r.data_emissao) : null],
          ['No portal', r.disponibilizado_em ? `desde ${data(r.disponibilizado_em)}` : 'não'],
        ]} />
      </>
    ),
    acoes: (
      <>
        <Link className="botao secundario" to={`/os/${r.os_id}`}>Abrir OS</Link>
        {r.cptu_campanha_id && (
          <Link className="botao secundario" to={`/cptu/${r.cptu_campanha_id}/relatorio`}>Abrir a campanha</Link>
        )}
        {r.cptu_campanha_id && r.tem_pdf && (
          <LinkProtegido className="botao" caminho={caminhoDoPdfEmitido(r.id)}
            nomeQueda={`${r.numero ?? 'relatorio'}-${rev(r.revisao)}.pdf`}>
            Relatório PDF
          </LinkProtegido>
        )}
        {/* Onda 328 (DOC-01): o emitido sai pelos bytes guardados DAQUELA
            revisão; só o aprovado-sem-emissão é gerado na hora, preliminar. */}
        {r.ensaio_id && !r.cptu_campanha_id && r.tem_pdf && (
          <LinkProtegido className="botao" caminho={`/relatorios/${r.id}/pdf`}
            nomeQueda={`${r.numero ?? 'laudo'}-${rev(r.revisao)}.pdf`}>
            Laudo PDF
          </LinkProtegido>
        )}
        {r.ensaio_id && !r.cptu_campanha_id && !r.tem_pdf && (
          <LinkProtegido className="botao secundario" caminho={`/ensaios/${r.ensaio_id}/laudo.pdf`}
            nomeQueda={`${r.numero ?? 'laudo'}-PRELIMINAR.pdf`}>
            Laudo preliminar
          </LinkProtegido>
        )}
      </>
    ),
  };
}
